import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { apiCall } from 'utils/apiClient';

const priceTypeOptions = ['unit', 'square_feet'];
const BASE_URL = process.env.REACT_APP_HAPS_MAIN_BASE_URL || '';

const resolveImageUrl = (value) => {
    if (!value) return '';
    if (/^https?:\/\//i.test(value)) return value;
    return `${BASE_URL.replace(/\/$/, '')}/${value.replace(/^\//, '')}`;
};

const Addon = ({ addons, setAddons }) => {
    const handleDeleteImage = async (index) => {
        const imageToDelete = addons[index]?.image;
        if (!imageToDelete) {
            toast.error('No image to delete.');
            return;
        }
        try {
            const result = await apiCall({
                endpoint: 'services/delete-image',
                method: 'POST',
                data: { image_path: imageToDelete },
            });
            if (result && (result.status === 200 || result.status === 404)) {
                const updatedAddons = [...addons];
                updatedAddons[index].image = '';
                setAddons(updatedAddons);
                setPendingImages((prev) => ({ ...prev, [index]: null }));
                toast.success('Image deleted successfully!');
            } else {
                toast.error('Failed to delete image.');
            }
        } catch (error) {
            console.error('Error deleting image:', error);
            toast.error('Error while deleting image.');
        }
    };
    const [pendingImages, setPendingImages] = useState({});

    const handleAddAddon = () => {
        setAddons([
            ...addons,
            {
                group_name: '',
                name: '',
                price_type: '',
                qty: '',
                price: '',
                description: '',
                partner_price: '',
                is_required: 0,
                image: '',
            },
        ]);
    };

    const handleRemoveAddon = (index) => {
        const updatedAddons = addons.filter((_, i) => i !== index);
        setAddons(updatedAddons);
        setPendingImages((prev) => {
            const next = {};
            Object.keys(prev).forEach((key) => {
                const oldIndex = Number(key);
                if (oldIndex < index) next[oldIndex] = prev[key];
                if (oldIndex > index) next[oldIndex - 1] = prev[key];
            });
            return next;
        });
    };

    const handleAddonChange = (index, field, value) => {
        const updatedAddons = [...addons];
        updatedAddons[index][field] = value;
        setAddons(updatedAddons);
    };

    const handleAddonImageSelect = (index, file) => {
        setPendingImages((prev) => ({
            ...prev,
            [index]: file || null,
        }));
    };

    const handleAddonImageUpload = async (index) => {
        const file = pendingImages[index];

        if (!file) {
            toast.error('Please select an image first.');
            return;
        }

        const formBody = new FormData();
        formBody.append('images[]', file);

        try {
            const result = await apiCall({
                endpoint: 'services/upload-image',
                method: 'POST',
                data: formBody,
            });

            const uploadedPath = result?.image_urls?.[0];
            if (result?.status === 200 && uploadedPath) {
                handleAddonChange(index, 'image', uploadedPath);
                setPendingImages((prev) => ({
                    ...prev,
                    [index]: null,
                }));
                toast.success('Addon image uploaded.');
            } else {
                toast.error('Addon image upload failed.');
            }
        } catch (error) {
            console.error('Error uploading addon image:', error);
            toast.error('Error while uploading addon image.');
        }
    };

    return (
        <div className="space-y-4 mt-4 px-4">
            <div className="text-lg font-semibold">Addons</div>
            {addons.map((addon, index) => (
                <div key={index} className="space-y-2 border-b pb-4 mb-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-center px-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Group Name</label>
                            <input
                                type="text"
                                className="w-full border rounded p-2 text-sm"
                                value={addon.group_name}
                                onChange={(e) => handleAddonChange(index, 'group_name', e.target.value)}
                                required
                                placeholder='Enter group name'
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">Addon Name</label>
                            <input
                                type="text"
                                className="w-full border rounded p-2 text-sm"
                                value={addon.name}
                                onChange={(e) => handleAddonChange(index, 'name', e.target.value)}
                                required
                                placeholder='Enter addon name'
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">Price Type</label>
                            <select
                                className="w-full border rounded p-2 text-sm"
                                value={addon.price_type}
                                onChange={(e) => handleAddonChange(index, 'price_type', e.target.value)}
                                required
                            >
                                <option value="">Select</option>
                                {priceTypeOptions.map((type) => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">Qty</label>
                            <input
                                type="number"
                                className="w-full border rounded p-2 text-sm"
                                value={addon.qty}
                                onChange={(e) => handleAddonChange(index, 'qty', e.target.value)}
                                required
                                placeholder='Enter quantity'
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-center px-4 mt-2">
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">Price</label>
                            <input
                                type="number"
                                className="w-full border rounded p-2 text-sm"
                                value={addon.price}
                                onChange={(e) => handleAddonChange(index, 'price', e.target.value)}
                                required
                                placeholder='Enter price'
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">Partner Price</label>
                            <input
                                type="number"
                                className="w-full border rounded p-2 text-sm"
                                value={addon.partner_price}
                                onChange={(e) => handleAddonChange(index, 'partner_price', e.target.value)}
                                placeholder='Enter partner price'
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">Image</label>
                            <input
                                type="file"
                                accept="image/*"
                                className="w-full border rounded p-2 text-sm"
                                onChange={(e) => handleAddonImageSelect(index, e.target.files?.[0])}
                            />
                            <div className="flex items-center gap-2 mt-2">
                                <button
                                    type="button"
                                    onClick={() => handleAddonImageUpload(index)}
                                    className="text-xs border border-blue-500 text-blue-600 px-2 py-1 rounded hover:bg-blue-50 transition"
                                >
                                    Upload
                                </button>
                                {addon.image && (
                                    <>
                                        <a
                                            href={resolveImageUrl(addon.image)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-xs text-green-600 underline"
                                        >
                                            View
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteImage(index)}
                                            className="text-xs border border-red-500 text-red-600 px-2 py-1 rounded hover:bg-red-50 transition"
                                        >
                                            Delete
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">Description</label>
                            <input
                                type="text"
                                className="w-full border rounded p-2 text-sm"
                                value={addon.description}
                                onChange={(e) => handleAddonChange(index, 'description', e.target.value)}
                                required
                                placeholder='Enter description'
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-between gap-4 mt-2 px-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">Required</label>
                            <input
                                type="checkbox"
                                checked={parseInt(addon.is_required)}
                                onChange={(e) =>
                                    handleAddonChange(index, 'is_required', e.target.checked ? 1 : 0)
                                }
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => handleRemoveAddon(index)}
                            className="text-red-500 text-xs border border-red-400 px-3 py-1 rounded hover:bg-red-50 transition"
                        >
                            Remove
                        </button>
                    </div>
                </div>
            ))}

            <button
                type="button"
                onClick={handleAddAddon}
                className="bg-indigo-500 text-white px-4 py-2 rounded text-sm"
            >
                + Add Addon
            </button>
        </div>
    );
};

Addon.propTypes = {
    addons: PropTypes.arrayOf(
        PropTypes.shape({
            group_name: PropTypes.string,
            name: PropTypes.string,
            price_type: PropTypes.string,
            qty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            partner_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            description: PropTypes.string,
            image: PropTypes.string,
            is_required: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
        })
    ).isRequired,
    setAddons: PropTypes.func.isRequired,
};

export default Addon;
